import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { 
  FaEnvelope, 
  FaCalendar, 
  FaSearch,
  FaTrash,
  FaChartBar,
  FaUsers
} from 'react-icons/fa';
import { newsletterService } from '../../services/newsletterService';
import LogoLoader from "../Common/LogoLoader";

const NewsletterManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({
    status: 'active',
    page: 1,
    limit: 10
  });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 450);
  const [pagination, setPagination] = useState({});
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    loadSubscribers();
  }, [filters, debouncedSearch]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      const response = await newsletterService.getAllSubscribers({
        ...filters,
        search: debouncedSearch,
      });
      console.log('Newsletter response:', response.data);
      setSubscribers(response.data?.data?.subscribers || response.data?.subscribers || []);
      setPagination(response.data?.data?.pagination || response.data?.pagination || {});
    } catch (error) {
      console.error('Error loading subscribers:', error);
      setMessage('Error loading newsletter subscribers');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await newsletterService.getNewsletterStats();
      console.log('Newsletter stats response:', response.data);
      setStats(response.data?.data || response.data || {});
    } catch (error) {
      console.error('Error loading newsletter stats:', error);
    }
  };

  const handleDelete = async (subscriberId) => {
    if (window.confirm('Are you sure you want to delete this subscriber?')) {
      try {
        await newsletterService.deleteSubscriber(subscriberId);
        setMessage('Subscriber deleted successfully');
        loadSubscribers();
        loadStats();
      } catch (error) {
        console.error('Error deleting subscriber:', error);
        setMessage('Error deleting subscriber');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoLoader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Newsletter Management</h1>
        <div className="flex space-x-4">
          <button
            onClick={() => setShowStats(!showStats)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <FaChartBar className="h-5 w-5 mr-2" />
            {showStats ? 'Hide Stats' : 'Show Stats'}
          </button>
          <div className="relative">
            <FaSearch className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search subscribers..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setFilters((f) => ({ ...f, page: 1 }));
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      {/* Stats Section */}
      {showStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FaUsers className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Subscribers</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalSubscribers || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FaCalendar className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Month</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.newSubscribersThisMonth || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FaEnvelope className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Week</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.subscribersThisWeek || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FaTrash className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Unsubscribed</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.totalUnsubscribed || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`p-4 rounded-md ${
          message.includes('Error') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
        }`}>
          {message}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Newsletter Subscribers ({pagination.total || 0})
          </h3>
        </div>
        
        <ul className="divide-y divide-gray-200">
          {subscribers.map((subscriber) => (
            <li key={subscriber._id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <FaEnvelope className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-lg font-medium text-gray-900">{subscriber.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        subscriber.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {subscriber.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {subscriber.source && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {subscriber.source}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <FaCalendar className="h-4 w-4 mr-2" />
                      Subscribed: {formatDate(subscriber.subscribedAt)}
                    </div>
                    {subscriber.unsubscribedAt && (
                      <div className="flex items-center">
                        <FaCalendar className="h-4 w-4 mr-2" />
                        Unsubscribed: {formatDate(subscriber.unsubscribedAt)}
                      </div>
                    )}
                  </div>
                  
                  {subscriber.tags && subscriber.tags.length > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-1">
                        {subscriber.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="ml-4">
                  <button
                    onClick={() => handleDelete(subscriber._id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current - 1) * pagination.limit) + 1} to {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilters({ ...filters, page: pagination.current - 1 })}
              disabled={pagination.current <= 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters({ ...filters, page: pagination.current + 1 })}
              disabled={pagination.current >= pagination.pages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManagement;
